import { client } from "@/apis/gql/apolloClient";
import type { Company } from "@/apis/gql/types";
import { ExportBackgroundJobStatusTracker } from "@/components/shared/errors/export-background-job-status";
import { BackgroundJobStatusTracker } from "@/components/shared/miscellaneous/background-jobs-status-tracker";
import { LoadingSpinner } from "@/components/ui/spinner";
import { BackgroundJobsProvider } from "@/providers/background-jobs";
import { InvoiceBrowserProvider } from "@/providers/invoice-browser";
import { InvoicePdfCacheProvider } from "@/providers/invoice-pdf-cache";
import { USER_COMPANIES_QUERY } from "@/queries/companies/get-all";
import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";

export const Route = createFileRoute("/_authenticated/$company")({
  beforeLoad: async ({ params, context }) => {
    const { data } = await client.query({
      query: USER_COMPANIES_QUERY,
    });

    const companies = data?.userCompanies ?? [];
    const company = companies.find(
      (c: Company) => c.companySlug === params.company,
    );

    if (!company) {
      throw redirect({
        to: "/403",
        search: {
          error: "http_errors.forbidden.tenant",
        },
      });
    }

    sessionStorage.setItem("tenantId", company.companyId);

    return { company, companies };
  },

  pendingComponent: () => (
    <div className="h-screen w-screen flex justify-center items-center">
      <LoadingSpinner />
    </div>
  ),

  component: CompanyLayout,
});

function CompanyLayout() {
  return (
    <InvoiceBrowserProvider>
      <InvoicePdfCacheProvider>
        <BackgroundJobsProvider>
          <Outlet />
          <div
            id="status-cards"
            className="fixed bottom-4 right-4 flex flex-col gap-4 z-50 w-[450px]"
          />
          <BackgroundJobStatusTracker />
          <ExportBackgroundJobStatusTracker />
        </BackgroundJobsProvider>
      </InvoicePdfCacheProvider>
    </InvoiceBrowserProvider>
  );
}

export function useCompany() {
  return Route.useRouteContext().company;
}

export function useCompanies() {
  return Route.useRouteContext().companies;
}
